//MIT License
//
//Part of this code is copyright (c) 2020 Devias
//
//Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
//The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
//
//THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
  Avatar,
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import { useSelector, useDispatch } from "react-redux"

const PIItypes = ['CPR', 'PII', '.. swedens CPR'];

const useStyles = makeStyles(() => ({
  '@global': {
    '.MuiMenuItem-root': {
      fontSize: 'clamp(0.72rem, 0.9vw, 0.875rem) !important',
    },
    '.MuiMenu-paper': {
      borderRadius: '14px !important',
    },
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#fb741a',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.72rem, 0.9vw, 0.875rem)',
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.72rem, 0.9vw, 0.875rem)',
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      backgroundColor: '#FFFFFF',
    },
    '& .MuiSelect-selectMenu': {
      minHeight: 'unset !important',
      paddingTop: '10.5px',
      paddingBottom: '10.5px',
      lineHeight: 'normal',
      backgroundColor: "transparent",
    },
  },
  root: {
    background: "#f7faff",
    boxShadow: "0 6px 32px rgba(0,45,85,0.18)",
    borderRadius: 14,
  },
  Button: {
    color: "#FFFFFF",
    backgroundColor: "#002D55",
    borderRadius: 18,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.39vw, 6px) clamp(10px, 1.04vw, 16px)',
    marginRight: 'clamp(6px, 0.52vw, 8px)',
    fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: "#fb741a",
    }
  },
  closeIconButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#fb741a',
      border: 'none',
      boxShadow: 'none',
    },
  },
  avatar: {
    backgroundColor: '#6a8caf',
    height: 36,
    width: 36,
    "@media (max-width:1920px)": {
      height: 29,
      width: 29,
    },
    "@media (max-width:1280px)": {
      height: 29 * 0.8,
      width: 29 * 0.8,
    },
    "@media (max-width:960px)": {
      height: 29 * 0.8,
      width: 29 * 0.8,
    },
    "@media (max-width:600px)": {
      height: 29 * 0.7,
      width: 29 * 0.7,
    },
  },
  card: {
    background: '#f7faff',
    borderRadius: 14,
  },
  cardContent: {
    background: '#f7faff',
    borderRadius: 14,
    fontSize: 'clamp(12px, 1vw, 16px)',
    padding: 18,
    paddingTop: 26,
    paddingBottom: 26, 
  },
  cardHeaderRoot: {
    height: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    minHeight: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    padding: '0 clamp(10px, calc(2px + 0.625vw), 16px)',
    boxSizing: 'border-box',
    borderBottom: '2px solid #fb741a',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    '& .MuiCardHeader-content': {
      overflow: 'hidden',
    },
  },
  cardHeaderTitle: {
    color: '#002D55',
    fontWeight: 'bold',
    fontSize: 'clamp(15px, 1.4vw, 22px)',
    paddingLeft: 'clamp(5px, calc(2px + 0.625vw), 5px)',
  },
}));

const ProfileDetails = ({ className, ...rest }) => {
  const [isLoading, setIsLoading] = useState(false)
  const classes = useStyles();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [cpr, setCpr] = useState('');
  const [country, setCountry] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [insurance, setInsurance] = useState('');
  const [PIItype, setPIItype] = useState("CPR");
  const [PIInumber, setPIInumber] = useState('');

  const [token, _] = useState(useSelector(state => state.auth.token))


  useEffect(() => {
    setIsLoading(true)
    fetchUserData()
  }, [])

  const fetchUserData = async () => {
    const response = await fetch("https://api.virtus-one.com/profile/profile/", {
      headers: {
        "Authorization": `Token ${token}`
      },
      method: "GET",
    });

    if (response.ok) {
      const data = await response.json();
      setEmail(data.profile.email)
      setFirstName(data.profile.first_name)
      setLastName(data.profile.last_name)

    } else {
      alert("ERROR")
    }
    setIsLoading(false)
  }

  const saveHandler = async () => {
    const data = {
      first_name: firstName,
      last_name: lastName,
      phone: phone,
      birthdate: birthdate,
      address: address,
      city: city,
      zip: zip,
      cpr: cpr,
      country: country,
      payment_status: paymentStatus,
      payment_method: paymentMethod,
      insurance: insurance,
      pii_type: PIItype,
      pii_number: PIInumber,
    }
    setIsLoading(true)
    const response = await fetch("https://api.virtus-one.com/profile/update/", {
      method: "PUT", 
      headers: {
        "content-type": "application/json",
        "Authorization": `Token ${token}`
      },
      body: JSON.stringify(data)
    })

    if (response.ok) {
      //
    } else {
      //
    }


  }



  return (
    <form
      autoComplete="off"
      noValidate
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeaderRoot}
          //avatar={<Avatar className={classes.avatar}><PersonIcon style={{ fontSize: 'clamp(24px, 2vw, 28px)' }} /></Avatar>}
          title={<span className={classes.cardHeaderTitle}>Profile</span>}
          style={{ marginBottom: 0 }}
        />
        <Divider style={{marginLeft: 0}}/>
        <CardContent className={classes.cardContent}>
          <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="First name" variant="outlined" value={firstName} onChange={e => setFirstName(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Last name" variant="outlined" value={lastName} onChange={e => setLastName(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Email Address" variant="outlined" value={email} onChange={e => setEmail(e.target.value)} disabled />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Phone" variant="outlined" value={phone} onChange={e => setPhone(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Address" variant="outlined" value={address} onChange={e => setAddress(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="City" variant="outlined" value={city} onChange={e => setCity(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="ZIP" variant="outlined" value={zip} onChange={e => setZip(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Country" variant="outlined" value={country} onChange={e => setCountry(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField 
                className={classes.orangeFocused} size="small" margin="none"
                select
                fullWidth
                label="PII Type"
                variant="outlined"
                value={PIItype}
                onChange={e => setPIItype(e.target.value)}
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    PaperProps: {
                      style: { borderRadius: 14, marginTop: 8 },
                    },
                  },
                }}
              >
                <MenuItem value="CPR">CPR</MenuItem>
                <MenuItem value="PII">PII</MenuItem>
                <MenuItem value=".. swedens CPR">.. swedens CPR</MenuItem>
              </TextField>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="PII Number" variant="outlined" value={PIInumber} onChange={e => setPIInumber(e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <Box
          display="flex"
          justifyContent="flex-end"
          p={2}
        >
          <Button
            className={classes.Button}
            variant="contained"
            onClick={saveHandler}
          >
            Save details
          </Button>
        </Box>
      </Card>
    </form>
  );
};

ProfileDetails.propTypes = {
  className: PropTypes.string
};

export default ProfileDetails;